// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';
import type { Database } from '@/types/supabase';

// Get environment variables before any other imports that might use them
const RAW_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Only import after we have the variables
import { getDynamicUrl, isNetworkAccess } from '@/lib/network-utils';
// DO NOT import validateSupabaseEnv here - causes circular dependency
// Validation is called explicitly from App.tsx
import { getClerkSupabaseJwt, isClerkEnabled, shouldSkipAuth } from '@/lib/auth';

const SUPABASE_URL = getDynamicUrl(RAW_SUPABASE_URL);

// SECURITY: Debug logging removed to prevent environment metadata exposure
// Enable only with explicit debug flag in development if needed
if (import.meta.env.DEV && import.meta.env.VITE_DEBUG === 'true') {
  // eslint-disable-next-line no-console
  console.log('🔍 Debug mode enabled');
}

const PRIMARY_SUPABASE_KEY = SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !PRIMARY_SUPABASE_KEY) {
  // eslint-disable-next-line no-console
  console.error('❌ Supabase initialization failed:', {
    urlMissing: !SUPABASE_URL,
    keyMissing: !PRIMARY_SUPABASE_KEY,
    urlValue: SUPABASE_URL || 'undefined',
    keyLength: PRIMARY_SUPABASE_KEY?.length || 0,
  });
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Defer network access logging to avoid logger initialization issues
// This is now called from getNetworkInfo() function below

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Check if we should attach Clerk JWTs
const clerkEnabled = isClerkEnabled();
const skipAuth = shouldSkipAuth();
const shouldAttachClerkJwt = clerkEnabled && !skipAuth;

function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function resolveClerkToken(options: { forceRefresh?: boolean } = {}): Promise<string | null> {
  if (!shouldAttachClerkJwt) {
    return null;
  }

  const { forceRefresh = false } = options;
  const MAX_ATTEMPTS = 2;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const token = await getClerkSupabaseJwt({ forceRefresh: forceRefresh || attempt > 0 });
    if (token) {
      return token;
    }

    // Small delay before retrying if the session is still initializing
    await wait(150);
  }

  throw new Error('Unable to obtain Clerk Supabase token after retries');
}

// SECURITY: Service role key must NEVER be used in frontend code
// Even in local development, use anon key with proper RLS policies

// Helper to get network info (lazy to avoid early logger calls)
function getNetworkInfo() {
  if (import.meta.env.DEV) {
    logger.log('🔗 Supabase URL:', SUPABASE_URL);
    if (isNetworkAccess()) {
      logger.log('📡 Network access detected - URLs adjusted for IP:', window.location.hostname);
    }

    logger.debug('Supabase client configuration:', {
      skipAuth,
      url: SUPABASE_URL,
      usingKey: 'anon', // Always use anon key in frontend
      shouldAttachClerkJwt,
    });
  }
}

// Configure Supabase client options
// Always disable auth features since we're using external auth (Clerk)
const supabaseOptions: any = {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
  global: {
    headers: {
      Accept: 'application/json',
      // Don't set Content-Type globally - let each request set it appropriately
      // This is especially important for file uploads which need multipart/form-data
    },
  },
};

// Configure authentication headers
supabaseOptions.global.headers = {
  ...supabaseOptions.global.headers,
  apikey: PRIMARY_SUPABASE_KEY,
};

if (skipAuth && PRIMARY_SUPABASE_KEY) {
  supabaseOptions.global.headers = {
    ...supabaseOptions.global.headers,
    Authorization: `Bearer ${PRIMARY_SUPABASE_KEY}`,
  };
}

if (shouldAttachClerkJwt) {
  logger.debug('Configuring Supabase client to use Clerk JWT access tokens');
  supabaseOptions.accessToken = async () => {
    const token = await resolveClerkToken();
    if (!token) {
      logger.warn('Clerk Supabase token unavailable for authenticated request');
      throw new Error('Missing Clerk authentication token');
    }
    return token;
  };

  // Also configure realtime to use access token for WebSocket connections
  supabaseOptions.realtime = {
    params: {
      eventsPerSecond: 10,
    },
  };
} else if (skipAuth) {
  logger.debug('Skip-auth mode detected – using anon role authentication');
}

// SECURITY: Always use anon key in frontend - NEVER service role key
const supabaseKey = PRIMARY_SUPABASE_KEY;

// Defer logging to avoid initialization issues
// Will be called from createSupabaseClient

// Singleton instance to ensure only one client is created
let supabaseInstance: ReturnType<typeof createClient<Database>> | null = null;

// Create the Supabase client with appropriate authentication
// Using singleton pattern to ensure consistent configuration
const createSupabaseClient = () => {
  if (!supabaseInstance) {
    // Log configuration info (only in development, after logger is ready)
    if (skipAuth) {
      logger.debug('Creating Supabase client with:', {
        keyType: 'anon', // Always anon in frontend
        keyPrefix: supabaseKey?.substring(0, 20) + '...',
      });
    }
    logger.debug('Creating new Supabase client instance');
    supabaseInstance = createClient<Database>(SUPABASE_URL, supabaseKey, supabaseOptions);

    // Log network info after creation
    getNetworkInfo();
  }
  return supabaseInstance;
};

// Export the singleton instance
export const supabase = createSupabaseClient();

/**
 * Ensure Supabase Realtime uses the current Clerk JWT.
 * REST queries use the accessToken callback, but broadcast/fetch requests read
 * the realtime client's auth value, so keep it in sync before opening channels.
 */
export async function refreshSupabaseRealtimeAuth(
  options: { forceRefresh?: boolean } = {},
): Promise<boolean> {
  if (!shouldAttachClerkJwt) {
    return true;
  }

  const token = await resolveClerkToken(options);
  if (!token) {
    return false;
  }

  // Call without an explicit token so Realtime continues to use the accessToken
  // callback. Passing a token manually disables callback refresh inside
  // realtime-js, which can leave channels with an expired Clerk JWT.
  await supabase.realtime.setAuth();
  return true;
}

/**
 * Ensure the Supabase client is ready for authenticated operations.
 * In skip-auth mode this simply resolves immediately.
 */
export async function getAuthenticatedSupabaseClient() {
  if (shouldAttachClerkJwt) {
    await resolveClerkToken();
  }
  return supabase;
}

// Wrap functions.invoke to ensure Clerk JWT is included
const originalInvoke = supabase.functions.invoke.bind(supabase.functions);
supabase.functions.invoke = async function (functionName: string, options?: any) {
  // Only add Clerk JWT in production/staging environments
  if (shouldAttachClerkJwt) {
    try {
      const token = await resolveClerkToken();
      if (token) {
        // Ensure headers object exists
        options = options || {};
        options.headers = options.headers || {};

        // Only set Authorization if not already provided
        if (!options.headers.Authorization) {
          logger.debug(`Adding Clerk JWT to edge function call: ${functionName}`);
          options.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (error) {
      logger.error(`Failed to attach Clerk token for edge function ${functionName}`, error);
      throw error;
    }
  }

  return originalInvoke(functionName, options);
};

// Wrap the client for API tracking in development
// TEMPORARILY DISABLED - causing initialization issues
// TODO: Fix Supabase interceptor to not break client initialization
/*
if (import.meta.env.DEV) {
  // Delay wrapping to ensure client is fully initialized
  setTimeout(() => {
    import('@/lib/supabaseInterceptor').then(({ wrapSupabaseClient }) => {
      try {
        wrapSupabaseClient(supabase);
        logger.debug('Supabase client wrapped for monitoring');
      } catch (error) {
        logger.error('Error wrapping Supabase client:', error);
      }
    }).catch(err => {
      logger.debug('Could not import supabaseInterceptor:', err);
    });
  }, 100); // Small delay to ensure client is ready
}
*/
